import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getAllPosts, formatPostDate, AUTHOR_NAME } from '../../blog';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BlogPage.module.css';

/**
 * The journal index - every guide we have published, newest first.
 *
 * The list is rendered server side from src/blog/posts.json, so search engines
 * and AI crawlers get the full index without running any JavaScript.
 */
const BlogPage = () => {
  const intl = useIntl();
  const config = useConfiguration();
  const scrollingDisabled = useSelector(isScrollingDisabled);

  const posts = getAllPosts();

  // Free text filter. The unfiltered list is what renders server side, so
  // crawlers and AI readers still receive every post; filtering only ever
  // happens in the browser after someone types.
  const [query, setQuery] = useState('');
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const matches = post => {
    if (!words.length) return true;
    const hay = [
      post.title,
      post.description,
      post.topic,
      (post.keywords || []).join(' '),
      (post.summary || []).join(' '),
    ]
      .join(' ')
      .toLowerCase();
    // every word must appear, so extra words narrow the list instead of widening it
    return words.every(w => hay.includes(w));
  };
  const visible = posts.filter(matches);
  const title = intl.formatMessage({ id: 'BlogPage.title' });
  const description = intl.formatMessage({ id: 'BlogPage.description' });

  const rootURL = config.marketplaceRootURL;
  const blogURL = `${rootURL}/blog`;

  // Blog + ItemList so the whole index is machine readable from one fetch,
  // and a breadcrumb trail so the /blog level is understood as a section.
  const schema = [
    {
      '@type': 'Blog',
      '@id': blogURL,
      name: `${config.marketplaceName} Journal`,
      description,
      url: blogURL,
      inLanguage: 'en',
      publisher: {
        '@type': 'Organization',
        name: config.marketplaceName,
        url: rootURL,
      },
      blogPost: posts.map(post => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.description,
        url: `${rootURL}/blog/${post.slug}`,
        datePublished: post.published,
        dateModified: post.updated || post.published,
        author: { '@type': 'Person', name: AUTHOR_NAME },
      })),
    },
    {
      '@type': 'ItemList',
      itemListElement: posts.map((post, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${rootURL}/blog/${post.slug}`,
        name: post.title,
      })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: config.marketplaceName, item: rootURL },
        { '@type': 'ListItem', position: 2, name: 'Journal', item: blogURL },
      ],
    },
  ];

  return (
    <Page
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
      schema={schema}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <header className={css.header}>
            <p className={css.eyebrow}>Nextmine · Journal</p>
            <h1 className={css.title}>Guides for collectors</h1>
            <p className={css.intro}>
              How to read a hallmark, date a piece, recognise an old cut diamond and sell what you
              own without getting burned. Written for people who buy and sell jewelry that has
              already had one life.
            </p>
            <p className={css.byline}>Written by {AUTHOR_NAME}</p>
          </header>

          <form
            className={css.find}
            role="search"
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <label className={css.findLabel} htmlFor="blog-q">
              What are you looking for?
            </label>
            <input
              id="blog-q"
              type="search"
              className={css.findInput}
              value={query}
              autoComplete="off"
              spellCheck="false"
              placeholder="What are you looking for?"
              onChange={e => setQuery(e.target.value)}
            />
          </form>
          <p className={css.findHint}>
            Searches titles, summaries and topics. Try <b>escrow</b>, <b>hallmarks</b> or{' '}
            <b>shipping</b>.
          </p>
          {words.length ? (
            <p className={css.findCount} role="status" aria-live="polite">
              {visible.length} {visible.length === 1 ? 'guide' : 'guides'} matching &ldquo;
              {query.trim()}&rdquo;
            </p>
          ) : null}
          {words.length && visible.length === 0 ? (
            <p className={css.findNone}>
              Nothing matched. The journal covers buying, selling, dating and valuing jewelry, so a
              very specific piece or maker may not have its own guide yet.
            </p>
          ) : null}

          <ul className={css.list}>
            {visible.map(post => (
              <li key={post.slug} className={css.item}>
                <article className={css.card}>
                  {post.image ? (
                    <NamedLink
                      name="BlogPostPage"
                      params={{ slug: post.slug }}
                      className={css.thumbLink}
                    >
                      {/* Decorative here: the heading right next to it already
                          links to the same post and names it. */}
                      <img
                        className={css.thumb}
                        src={post.image.src}
                        width={post.image.width}
                        height={post.image.height}
                        alt=""
                        loading="lazy"
                      />
                    </NamedLink>
                  ) : null}
                  <p className={css.meta}>
                    <span className={css.topic}>{post.topic}</span>
                    <span className={css.dot}>·</span>
                    <time dateTime={post.published}>{formatPostDate(post.published)}</time>
                    <span className={css.dot}>·</span>
                    <span>{post.readingMinutes} min read</span>
                  </p>
                  <h2 className={css.cardTitle}>
                    <NamedLink
                      name="BlogPostPage"
                      params={{ slug: post.slug }}
                      className={css.cardLink}
                    >
                      {post.title}
                    </NamedLink>
                  </h2>
                  <p className={css.excerpt}>{post.description}</p>
                  <NamedLink
                    name="BlogPostPage"
                    params={{ slug: post.slug }}
                    className={css.readMore}
                  >
                    Read the guide
                  </NamedLink>
                </article>
              </li>
            ))}
          </ul>

          <p className={css.outro}>
            Looking for something specific?{' '}
            <NamedLink name="SearchPage" className={css.link}>
              Browse the pieces for sale
            </NamedLink>{' '}
            or read the{' '}
            <NamedLink name="FaqPage" className={css.link}>
              questions and answers
            </NamedLink>{' '}
            about buying and selling here.
          </p>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BlogPage;
